import { useNavigate } from 'react-router-dom';
import { ShieldCheck, HeartPulse, AlertCircle } from 'lucide-react';

import { useApiData } from '../lib/hooks.js';
import { Card, Spinner, PageHeader, SectionTitle } from '../components/ui.jsx';

// Why the programme leaves certain things out. Worth having somewhere she can
// read it, and worth being able to show a clinician.

export default function Safety() {
  const navigate = useNavigate();
  const { data, loading } = useApiData('/training/plan');

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader title="How this plan is built" subtitle="What it avoids, and why." back={() => navigate(-1)} />

      <div className="px-gutter">
        <Card className="mb-4">
          <div className="flex gap-3">
            <ShieldCheck size={20} className="text-accent shrink-0 mt-0.5" />
            <div>
              <h2 className="text-card-title font-display mb-1.5">Built around your bones</h2>
              <p className="text-body-sm text-textSoft">
                Resistance training and weight-bearing work are among the most useful things you can do for bone. What matters is that
                the load goes up gradually and the spine stays neutral under it — which is why this plan looks slow at the start and why
                a few familiar exercises are missing altogether.
              </p>
            </div>
          </div>
        </Card>

        <SectionTitle>Deliberately left out</SectionTitle>
        {loading && <Spinner />}
        <div className="space-y-3 mb-5">
          {(data?.avoided || []).map((a) => (
            <Card key={a.what}>
              <h3 className="text-body font-semibold mb-1.5">{a.what}</h3>
              <p className="text-body-sm text-textSoft">{a.why}</p>
            </Card>
          ))}
        </div>

        <SectionTitle>Fuelling and migraines</SectionTitle>
        <Card className="mb-5">
          <div className="flex gap-3">
            <HeartPulse size={20} className="text-accent shrink-0 mt-0.5" />
            <div className="space-y-2.5">
              <p className="text-body-sm text-textSoft">
                Training while under-fuelled or short of fluid is a known trigger for you, so this app will never tell you to push
                through hunger, train fasted, or eat less because a week has been slow.
              </p>
              <p className="text-body-sm text-textSoft">
                If you are hungry before a session, eat something. If you have had a migraine, the session moves — it does not become a
                debt to pay back.
              </p>
              <p className="text-body-sm text-textSoft">
                The calorie range has a floor built into it. The app cannot set it below that floor, whatever the numbers do.
              </p>
            </div>
          </div>
        </Card>

        <SectionTitle>What this app will not do</SectionTitle>
        <Card className="mb-8">
          <div className="flex gap-3">
            <AlertCircle size={20} className="text-warn shrink-0 mt-0.5" />
            <ul className="space-y-2 text-body-sm text-textSoft">
              <li>It does not diagnose anything, and it does not decide between perimenopause, POI and early menopause.</li>
              <li>It does not touch HRT, supplements or any other medication, or comment on dosing.</li>
              <li>It does not interpret fertility results or prognosis.</li>
              <li>It does not treat or advise on osteoporosis beyond choosing exercises sensibly.</li>
              <li>It does not change a target on its own — it asks, gives its reason, and takes no for an answer.</li>
            </ul>
          </div>
          <p className="text-meta text-textMute mt-4 pt-3 border-t border-edge">
            All of that stays with your GP, your specialists and your clinic. If anything here conflicts with what they have told you,
            they are right and this app is wrong.
          </p>
        </Card>
      </div>
    </div>
  );
}
